import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { addDays, format } from 'date-fns';
import { BehaviorSubject } from 'rxjs';
import { utcDifferenceInDays } from 'server/helpers/utcDifferenceInDays';
import {
  EmploymentAvailabilityDay,
  EmploymentAvailabilityStatusEnum,
  PoolerUser,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';

@Component({
  selector: 'availability-percentage-bar',
  templateUrl: 'availability-percentage-bar.html',
  styleUrls: ['availability-percentage-bar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityPercentageBar implements OnInit {
  user$ = new BehaviorSubject<PoolerUser | undefined>(undefined);
  @Input() set user(value: PoolerUser | undefined) {
    this.user$.next(value);
  }
  get user() {
    return this.user$.value;
  }

  externalAvailabilityDays$ = new BehaviorSubject<
    EmploymentAvailabilityDay[] | undefined
  >(undefined);
  @Input() set externalAvailabilityDays(
    value: EmploymentAvailabilityDay[] | undefined,
  ) {
    this.externalAvailabilityDays$.next(value);
  }
  get externalAvailabilityDays() {
    return this.externalAvailabilityDays$.value;
  }

  fromDate$ = new BehaviorSubject<Date | undefined>(undefined);
  @Input() set fromDate(value: Date | undefined) {
    this.fromDate$.next(value);
  }
  get fromDate() {
    return this.fromDate$.value;
  }

  toDate$ = new BehaviorSubject<Date | undefined>(undefined);
  @Input() set toDate(value: Date | undefined) {
    this.toDate$.next(value);
  }
  get toDate() {
    return this.toDate$.value;
  }

  availabilityPercentage$ = new BehaviorSubject<number>(0);
  @Input() set availabilityPercentage(value: number) {
    this.availabilityPercentage$.next(value);
  }
  get availabilityPercentage() {
    return this.availabilityPercentage$.value;
  }

  employmentId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set employmentId(value: string | undefined) {
    this.employmentId$.next(value);
  }
  get employmentId() {
    return this.employmentId$.value;
  }

  gradient$ = new BehaviorSubject<string>('');

  constructor() {}

  async ngOnInit() {
    if (
      (!this.user$.value && !this.externalAvailabilityDays$.value) ||
      !this.fromDate$.value ||
      !this.toDate$.value
    )
      return;
    let availabilityDays: EmploymentAvailabilityDay[] = [];
    if (this.user$.value?.employmentAvailabilityDays) {
      availabilityDays = this.user$.value.employmentAvailabilityDays;
    } else if (this.externalAvailabilityDays$.value) {
      availabilityDays = this.externalAvailabilityDays$.value;
    }

    const daysBetween = utcDifferenceInDays(
      this.toDate$.value,
      this.fromDate$.value,
    );
    const bars: {
      width: number;
      color: '#e3e4e8' | '#2a3e72';
    }[] = [];
    const dayPercentageValue = 100 / (daysBetween + 1);

    let currentBar:
      | {
          width: number;
          color: '#e3e4e8' | '#2a3e72';
        }
      | undefined;

    let lastDayAvailability: EmploymentAvailabilityDay | undefined;
    for (let i = 0; i < daysBetween + 1; i++) {
      const dateFormatted = format(
        addDays(this.fromDate$.value, i),
        dbDayDateToken,
      );
      const dayAvailability = availabilityDays.find(
        (availability) =>
          this.fromDate$.value && availability.date === dateFormatted,
      );
      if (
        (dayAvailability?.status !== lastDayAvailability?.status ||
          i === daysBetween) &&
        currentBar
      ) {
        bars.push(currentBar);
        currentBar = undefined;
      }

      if (!currentBar) {
        currentBar = {
          width: 1,
          color:
            dayAvailability?.status ===
              EmploymentAvailabilityStatusEnum.AVAILABLE ||
            (dayAvailability?.status ===
              EmploymentAvailabilityStatusEnum.WORKING &&
              dayAvailability.employmentId === this.employmentId$.value)
              ? '#2a3e72'
              : '#e3e4e8',
        };
      } else {
        currentBar.width++;
      }
      if (!dayAvailability) {
      }
      lastDayAvailability = dayAvailability;
    }
    let gradient = 'linear-gradient(to right, ';
    if (bars.length === 0) return;
    if (bars.length === 1) {
      gradient = `${bars[0].color}`;
    } else {
      let lastPercentage = 0;
      bars.forEach((bar, index) => {
        if (index === 0) {
          gradient += `${bar.color} ${bar.width * dayPercentageValue}%, `;
        } else if (index === bars.length - 1) {
          gradient += `${bar.color} ${lastPercentage}%)`;
        } else {
          gradient += `${bar.color} ${lastPercentage}%, ${bar.color} ${
            lastPercentage + bar.width * dayPercentageValue
          }%, `;
        }
        lastPercentage = lastPercentage + bar.width * dayPercentageValue;
      });
    }
    this.gradient$.next(gradient);
  }
}
